export const SUPPORTED_MARKETPLACE_CITIES = ["Anchorage", "Wasilla", "Palmer", "Fairbanks"];

export function extractMarketplaceCity(value = "") {
  const text = String(value || "").trim().toLowerCase();
  if (!text) return "";
  return SUPPORTED_MARKETPLACE_CITIES.find((city) => text.includes(city.toLowerCase())) || "";
}

export function parseMarketplaceSearch(value = "") {
  const raw = String(value || "").trim();
  const city = extractMarketplaceCity(raw);
  if (!city) return { city: "", query: raw };

  const cityPattern = new RegExp(`\\b${city}\\b`, "ig");
  const query = raw
    .replace(cityPattern, " ")
    .replace(/\b(?:ak|alaska)\b/ig, " ")
    .replace(/[,|]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return { city, query };
}

export function vendorMatchesLocation(vendor = {}, city = "") {
  const target = String(city || "").trim().toLowerCase();
  if (!target) return true;
  const haystack = [
    vendor.pickup_area,
    vendor.pickup_address,
    vendor.city,
    vendor.pickup_city,
    vendor.service_area,
    vendor.service_city,
    vendor.delivery_area,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(target);
}

export function normalizeMenuItem(item = {}) {
  return {
    id: item.id,
    name: item.name || "",
    price: Number(item.price),
    image_url: item.image_url || "",
    category: item.category || "",
    available: item.available !== false,
    description: item.description || "",
    tags: Array.isArray(item.tags) ? item.tags : [],
  };
}

export function vendorMatchesQuery(vendor = {}, menu = [], query = "") {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  const vendorText = [vendor.business_name, vendor.cuisine, vendor.bio]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  if (vendorText.includes(q)) return true;
  return menu.some((item) =>
    [item.name, item.category, item.description, ...(Array.isArray(item.tags) ? item.tags : [])]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(q)
  );
}

export function buildAvailableItems(vendors = [], menusByVendor = {}, options = {}) {
  const query = String(options.query || "").trim().toLowerCase();
  const cuisine = String(options.cuisine || "").trim();
  const category = String(options.category || "").trim().toLowerCase();
  const groups = [];

  for (const vendor of vendors || []) {
    if (!vendor?.id || vendor.is_open === false) continue;
    if (cuisine && vendor.cuisine !== cuisine) continue;

    const items = [];
    for (const raw of menusByVendor[vendor.id] || []) {
      const item = normalizeMenuItem(raw);
      if (!item.id || !item.name || !item.available || !Number.isFinite(item.price) || item.price < 0.5) continue;
      if (category && String(item.category || "").trim().toLowerCase() !== category) continue;

      if (query) {
        const haystack = [
          item.name,
          item.category,
          item.description,
          ...(Array.isArray(item.tags) ? item.tags : []),
          vendor.business_name,
          vendor.cuisine,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!haystack.includes(query)) continue;
      }

      items.push({
        ...item,
        vendor_id: vendor.id,
        vendor_name: vendor.business_name || "Local kitchen",
        vendor_cuisine: vendor.cuisine || "",
        vendor_is_open: true,
        vendor_rating: Number(vendor.avg_rating || 0),
        vendor_review_count: Number(vendor.review_count || 0),
        vendor_pickup_area: vendor.pickup_area || vendor.pickup_city || vendor.city || "",
        accepts_pickup: vendor.accepts_pickup !== false,
        accepts_delivery: Boolean(
          vendor.accepts_vendor_delivery
          || (vendor.accepts_arctic_plate_delivery !== false && vendor.arctic_plate_delivery_admin_approved)
        ),
      });
    }

    items.sort((a, b) => {
      if (Boolean(a.image_url) !== Boolean(b.image_url)) return a.image_url ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
    if (items.length) groups.push(items);
  }

  const rows = [];
  for (let depth = 0; ; depth += 1) {
    let added = false;
    for (const items of groups) {
      if (items[depth]) {
        rows.push(items[depth]);
        added = true;
      }
    }
    if (!added) break;
  }
  return rows;
}


export function itemMatchesMarketplaceFilters(item = {}, options = {}) {
  const fulfillment = String(options.fulfillment || "");
  const dietary = String(options.dietary || "").trim().toLowerCase();
  const price = String(options.price || "");

  if (fulfillment === "pickup" && !item.accepts_pickup) return false;
  if (fulfillment === "delivery" && !item.accepts_delivery) return false;

  if (price) {
    const amount = Number(item.price);
    if (!Number.isFinite(amount)) return false;
    if (price === "under15" && amount >= 15) return false;
    if (price === "15to25" && (amount < 15 || amount > 25)) return false;
    if (price === "25plus" && amount < 25) return false;
  }

  if (dietary) {
    const text = [
      item.name,
      item.category,
      item.description,
      ...(Array.isArray(item.tags) ? item.tags : []),
    ].filter(Boolean).join(" ").toLowerCase();

    const aliases = {
      "gluten free": ["gluten free", "gluten-free", "gf"],
      vegetarian: ["vegetarian"],
      vegan: ["vegan"],
      halal: ["halal"],
    };
    const needles = aliases[dietary] || [dietary];
    if (!needles.some((needle) => text.includes(needle))) return false;
  }

  return true;
}


export function filterVendorsByMarketplaceItems(vendors = [], items = [], active = false) {
  if (!active) return vendors;
  const matchingVendorIds = new Set((items || []).map((item) => item?.vendor_id).filter(Boolean));
  return (vendors || []).filter((vendor) => vendor?.id && matchingVendorIds.has(vendor.id));
}
